import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, FileText, Gauge } from "lucide-react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";

const PLAN_ID = "standard";

export default function Billing() {
  const [plans, setPlans] = useState({});
  const [usage, setUsage] = useState(null);
  const [params] = useSearchParams();
  const [statusMsg, setStatusMsg] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const [poMode, setPoMode] = useState(false);
  const [poForm, setPoForm] = useState({ plan_id: PLAN_ID, agency_name: "", contact_name: "", contact_email: "", contact_phone: "", notes: "" });
  const [poSubmitted, setPoSubmitted] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then((r) => {
      setPlans(r.data.plans || {});
      setUsage(r.data.usage || null);
    });
  }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      setStatusMsg("Checkout cancelled. Your plan was not changed.");
      setStatusTone("warning");
      return;
    }
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatusMsg("Payment successful — your subscription is active.");
          setStatusTone("success");
          return;
        }
        if (data.status === "expired") {
          setStatusMsg("Session expired. Please try again.");
          setStatusTone("danger");
          return;
        }
        if (attempts++ < 5) setTimeout(poll, 2000);
        else setStatusMsg("Payment status pending — check back shortly.");
      } catch (_err) {
        setStatusMsg("Could not verify payment.");
        setStatusTone("danger");
      }
    };
    setStatusMsg("Verifying payment…");
    poll();
  }, [params]);

  const checkout = async () => {
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/billing/checkout", { plan_id: PLAN_ID, origin_url });
      window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || "Checkout failed");
    }
  };

  const submitPO = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/billing/po-request", poForm);
      setPoSubmitted(data);
    } catch (err) {
      alert(err.response?.data?.detail || "PO request failed");
    }
  };

  const plan = plans[PLAN_ID];

  return (
    <AppLayout>
      <div className="p-8 lg:p-10 max-w-[1300px]">
        <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">Billing</div>
        <h1 className="font-serif text-4xl tracking-tight font-medium mb-2" data-testid="billing-title">Plan &amp; procurement</h1>
        <p className="text-slate-600 mb-6">Pay by credit card via Stripe — or request a NET-30 PO/Invoice for government procurement.</p>

        {usage && (
          <div className="fa-card p-5 mb-6" data-testid="billing-usage-card">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest mb-3">
              <Gauge className="w-4 h-4" strokeWidth={1.75} />
              Current cycle usage
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-slate-500">Cases used</div>
                <div className="font-serif text-3xl" data-testid="usage-cases-used">{usage.cases_used}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Included</div>
                <div className="font-serif text-3xl">{usage.cases_included}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Remaining</div>
                <div className="font-serif text-3xl">{usage.cases_remaining}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Overage due</div>
                <div className="font-serif text-3xl" data-testid="usage-overage-amount">${Number(usage.overage_amount_due).toLocaleString()}</div>
              </div>
            </div>
            {usage.overage_cases > 0 && (
              <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 p-3">
                You&apos;ve exceeded the included <b>{usage.cases_included}</b> cases by <b>{usage.overage_cases}</b>. You&apos;ll receive a manual invoice for the overage above.
              </div>
            )}
          </div>
        )}

        {statusMsg && (
          <div className={`fa-card p-4 mb-6 ${statusTone === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : statusTone === "danger" ? "bg-red-50 border-red-200 text-red-800" : statusTone === "warning" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-blue-50 border-blue-200 text-blue-800"}`} data-testid="billing-status-msg">
            {statusMsg}
          </div>
        )}

        <div className="flex items-center gap-1 mb-8 border border-slate-200 inline-flex">
          <button data-testid="billing-mode-card" onClick={() => setPoMode(false)} className={`px-4 py-2 text-sm ${!poMode ? "bg-[#0B1B3D] text-white" : "bg-white text-slate-600"}`}>Credit Card</button>
          <button data-testid="billing-mode-po" onClick={() => setPoMode(true)} className={`px-4 py-2 text-sm ${poMode ? "bg-[#0B1B3D] text-white" : "bg-white text-slate-600"}`}>Purchase Order (NET-30)</button>
        </div>

        {!poMode ? (
          plan ? (
            <div className="fa-card grid grid-cols-1 md:grid-cols-12 max-w-4xl" data-testid="billing-plan-card">
              <div className="md:col-span-5 p-8 bg-[#0B1B3D] text-white">
                <div className="text-xs uppercase tracking-widest text-[#C5A059] mb-3">{plan.name}</div>
                <div className="font-serif text-5xl font-medium mb-2">${plan.price.toLocaleString()}</div>
                <div className="text-sm text-white/70 mb-6">per agency / year</div>
                <div className="text-xs text-white/60 border-t border-white/10 pt-4">
                  Includes {plan.limit_cases} cases per year. Additional cases billed at <span className="text-[#C5A059] font-medium">${plan.overage_price_per_case}</span> each via manual invoice.
                </div>
              </div>
              <div className="md:col-span-7 p-8 flex flex-col justify-center">
                <ul className="space-y-2.5 text-sm text-slate-700 mb-8">
                  {(plan.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#0B1B3D] mt-0.5 flex-shrink-0" strokeWidth={2} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button data-testid="checkout-standard" onClick={checkout} className="fa-btn-primary justify-center">
                  Subscribe with Stripe
                </button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">Loading plan…</div>
          )
        ) : poSubmitted ? (
          <div className="fa-card p-8 text-center">
            <div className="font-mono text-xs uppercase tracking-widest text-emerald-700 mb-2">PO Request Received</div>
            <h3 className="font-serif text-2xl mb-3">Reference: {poSubmitted.po_id}</h3>
            <p className="text-slate-600 max-w-lg mx-auto">{poSubmitted.message}</p>
          </div>
        ) : (
          <form onSubmit={submitPO} className="fa-card p-8 max-w-2xl space-y-5">
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Purchase Order / Invoice Request — NET-30</div>
            <p className="text-sm text-slate-600">For agencies using procurement workflows. Our team will email a formal quote within 2 business days.</p>
            <div>
              <label className="fa-label">Plan</label>
              <div className="fa-input max-w-xs bg-slate-50">{plan?.name || "Standard"} — ${(plan?.price || 3999).toLocaleString()}/yr</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="fa-label">Agency name</label><input data-testid="po-agency-name" required value={poForm.agency_name} onChange={(e) => setPoForm({ ...poForm, agency_name: e.target.value })} className="fa-input" /></div>
              <div><label className="fa-label">Contact name</label><input data-testid="po-contact-name" required value={poForm.contact_name} onChange={(e) => setPoForm({ ...poForm, contact_name: e.target.value })} className="fa-input" /></div>
              <div><label className="fa-label">Contact email</label><input data-testid="po-contact-email" type="email" required value={poForm.contact_email} onChange={(e) => setPoForm({ ...poForm, contact_email: e.target.value })} className="fa-input" /></div>
              <div><label className="fa-label">Contact phone</label><input data-testid="po-contact-phone" value={poForm.contact_phone} onChange={(e) => setPoForm({ ...poForm, contact_phone: e.target.value })} className="fa-input" /></div>
            </div>
            <div>
              <label className="fa-label">Notes</label>
              <textarea data-testid="po-notes" rows={3} value={poForm.notes} onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })} className="fa-input" />
            </div>
            <button data-testid="po-submit" type="submit" className="fa-btn-primary"><FileText className="w-4 h-4" /> Submit PO Request</button>
          </form>
        )}
      </div>
    </AppLayout>
  );
}
